/// <reference path="../../types/axios-fix.d.ts" />
import axios, { AxiosError } from 'axios';

export type ApiError = {
  message: string;
  code?: string;
  details?: string[];
  field?: string;
  status?: number;
};

export function extractErrorMessage(error: unknown): ApiError {
  // Type guard for AxiosError
  const isAxiosError = (err: any): err is AxiosError => {
    return err && typeof err === 'object' && err.response && err.config;
  };

  if (isAxiosError(error) && error.response?.data) {
    const responseData = error.response.data as any;
    
    // Check for nested 'error' object first (common pattern in this project)
    if (responseData.error && responseData.error.message) {
      return {
        message: responseData.error.message,
        code: responseData.error.code,
        details: responseData.error.details,
        field: responseData.error.field,
        status: error.response.status
      };
    }
    
    // Fallback: Check for message directly on data (less common but possible)
    if (responseData.message) {
      return {
        message: responseData.message,
        code: responseData.code,
        details: responseData.details,
        field: responseData.field,
        status: error.response.status
      };
    }
    
    // If neither structure is found, return a generic error
    return { 
      message: 'Unknown API error', 
      details: [JSON.stringify(error.response.data)],
      status: error.response.status
    };
  }

  // Check if the error object itself has the expected API error structure
  // (This handles errors transformed by the api.service interceptor)
  if (typeof error === 'object' && error !== null && 'message' in error && 'status' in error) {
    const apiError = error as ApiError; // Type assertion
    return {
      message: apiError.message,
      code: apiError.code,
      details: apiError.details,
      field: apiError.field,
      status: apiError.status
    };
  }
  
  if (error instanceof Error) {
    return { 
      message: error.message,
      code: 'UNKNOWN_ERROR',
      status: 500
    };
  }
  
  return { 
    message: 'An unknown error occurred',
    code: 'UNKNOWN_ERROR',
    status: 500
  };
}

export function formatError(error: unknown): ApiError {
  return extractErrorMessage(error);
}

/**
 * Extracts only the error message string from any error type.
 * Use this when you need to display errors in UI components.
 */
export function getErrorMessageString(error: unknown): string {
  const apiError = extractErrorMessage(error);
  return apiError.message;
}

/**
 * Safe console.error wrapper that prevents [object Object] display in webpack-dev-server.
 * Use this instead of console.error when logging errors.
 */
export function logError(context: string, error: unknown): void {
  const errorMessage = getErrorMessageString(error);
  const errorDetails = extractErrorMessage(error);
  
  // Log with string message first to prevent [object Object] in overlay
  console.error(`${context}: ${errorMessage}`);
  
  // Then log structured details for debugging
  if (errorDetails.status || errorDetails.code || errorDetails.details) {
    console.error('Error details:', {
      status: errorDetails.status,
      code: errorDetails.code,
      details: errorDetails.details
    });
  }
}

export function isNetworkError(error: unknown): boolean {
  // Type guard for AxiosError
  const isAxiosError = (err: any): err is AxiosError => {
    return err && typeof err === 'object' && err.response && err.config;
  };

  return (
    isAxiosError(error) && 
    (error.code === 'ECONNABORTED' || error.message.includes('Network Error'))
  );
}

export function isServerError(error: unknown): boolean {
  // Type guard for AxiosError
  const isAxiosError = (err: any): err is AxiosError => {
    return err && typeof err === 'object' && err.response && err.config;
  };

  return (
    isAxiosError(error) && 
    error.response?.status !== undefined && 
    error.response.status >= 500
  );
}

export function isAuthError(error: unknown): boolean {
  // Type guard for AxiosError
  const isAxiosError = (err: any): err is AxiosError => {
    return err && typeof err === 'object' && err.response && err.config;
  };

  return (
    isAxiosError(error) && 
    (error.response?.status === 401 || error.response?.status === 403)
  );
}

export function getErrorCode(error: unknown): string | undefined {
  // Type guard for AxiosError
  const isAxiosError = (err: any): err is AxiosError => {
    return err && typeof err === 'object' && err.response && err.config;
  };

  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data as any;
    return data?.code;
  }
  return undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  // Type guard for AxiosError
  const isAxiosError = (err: any): err is AxiosError => {
    return err && typeof err === 'object' && err.response && err.config;
  };

  if (isAxiosError(error) && error.response?.status) {
    return error.response.status;
  }
  return undefined;
}
